import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authApi } from '@/api/endpoints';
import { tokenStore } from '@/store/auth';
import { Input } from '@/components/ui/Input';
import { ApiClientError } from '@/api/client';
import type { AuthTokens } from '@/types/api';

// Public OAuth "Web application" client id (exposed in every browser by
// design — not a secret). Committed as the default so a plain git-push deploy
// activates Google sign-in without a build-env round-trip; VITE_GOOGLE_CLIENT_ID
// still overrides it (e.g. a staging client). Must match GOOGLE_CLIENT_ID on the
// API. Authorised JS origins on this client: app.puntsave.com.au + localhost:5173.
const GOOGLE_CLIENT_ID =
  (import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined) ||
  '273808011504-ghr1shsebljiq1uvrqt2so00m1ksukie.apps.googleusercontent.com';

/* Google's 4-colour G, identical to the prototype. */
const GoogleG = (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
  </svg>
);
const FacebookF = (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="#fff" aria-hidden="true">
    <path d="M24 12a12 12 0 1 0-13.88 11.85v-8.38H7.08V12h3.04V9.36c0-3 1.79-4.67 4.53-4.67 1.31 0 2.68.24 2.68.24v2.95h-1.51c-1.49 0-1.95.93-1.95 1.87V12h3.32l-.53 3.47h-2.79v8.38A12 12 0 0 0 24 12z" />
  </svg>
);
const MailIcon = (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#04240f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" />
  </svg>
);

export function LoginPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'welcome' | 'email'>('welcome');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const afterAuth = (tokens: AuthTokens) => {
    tokenStore.setTokens(tokens.accessToken, tokens.refreshToken);
    navigate('/bets');
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      afterAuth(await authApi.login({ email, password }));
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.error.code === 'invalid_credentials' ? 'Incorrect email or password.' : err.error.message);
      } else {
        setError('Network error — please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    setError(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const g = (window as any).google;
    if (!GOOGLE_CLIENT_ID || !g?.accounts?.id) {
      setError('Google sign-in isn’t enabled on this environment yet.');
      return;
    }
    g.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: async (resp: { credential?: string }) => {
        if (!resp.credential) {
          setError('Google sign-in was cancelled.');
          return;
        }
        try {
          afterAuth(await authApi.google({ credential: resp.credential }));
        } catch (err) {
          setError(
            err instanceof ApiClientError && err.error.code === 'use_existing_sign_in'
              ? 'This email already has a password account — sign in with email.'
              : 'Google sign-in failed. Please try again.',
          );
        }
      },
    });
    g.accounts.id.prompt();
  };

  const handleFacebook = () => setError('Facebook sign-in is coming soon.');

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-[560px] rounded-[24px] border border-white/[0.07] bg-brand-navy2 p-8 sm:p-11 shadow-[0_30px_80px_rgba(0,0,0,0.5)]">
        {/* Logo */}
        <div className="inline-flex items-center gap-2.5 mb-7">
          <span className="w-8 h-8 rounded-[9px] grid place-items-center bg-gradient-to-br from-brand-teal to-brand-mint text-brand-navy font-display font-extrabold text-lg">
            P
          </span>
          <span className="font-display font-extrabold tracking-[0.06em] text-[16px]">PUNTSAVE</span>
        </div>

        {mode === 'welcome' ? (
          <>
            <span className="self-start inline-block rounded-full px-3 py-1.5 mb-4 text-[11px] font-semibold uppercase tracking-[0.1em] text-brand-teal bg-brand-teal/10 border border-brand-teal/30">
              Now in Beta · Australia
            </span>

            <h1 className="font-display font-extrabold text-[40px] sm:text-[52px] leading-[0.98] tracking-[-0.03em] mb-4">
              Bet without losing.<br />
              <em className="not-italic text-brand-teal">Same thrill.</em>
              <br />
              <em className="not-italic text-brand-green">Different result.</em>
            </h1>

            <div className="flex flex-col gap-2.5 mt-6 mb-2">
              <button
                onClick={handleGoogle}
                className="flex items-center justify-center gap-2.5 w-full py-3.5 rounded-xl font-semibold text-[15px] bg-white text-[#1a1a1a] border border-white/15 hover:brightness-95 active:scale-[0.99] transition"
              >
                <span className="inline-flex w-5 h-5 items-center justify-center">{GoogleG}</span>
                Sign in with Google
              </button>
              <button
                onClick={handleFacebook}
                className="flex items-center justify-center gap-2.5 w-full py-3.5 rounded-xl font-semibold text-[15px] bg-[#1877f2] text-white hover:brightness-95 active:scale-[0.99] transition"
              >
                <span className="inline-flex w-5 h-5 items-center justify-center">{FacebookF}</span>
                Sign in with Facebook
              </button>
              <button
                onClick={() => { setError(null); setMode('email'); }}
                className="flex items-center justify-center gap-2.5 w-full py-3.5 rounded-xl font-semibold text-[15px] bg-brand-green text-[#04240f] hover:brightness-95 active:scale-[0.99] transition"
              >
                <span className="inline-flex w-5 h-5 items-center justify-center">{MailIcon}</span>
                Sign in with email
              </button>
            </div>

            {error && <p className="text-red-400 text-sm mt-3">{error}</p>}

            <p className="text-white/45 text-sm mt-6">
              New here?{' '}
              <Link to="/signup" className="text-brand-teal hover:brightness-110">Create an account</Link>
            </p>
          </>
        ) : (
          <>
            <button
              onClick={() => { setError(null); setMode('welcome'); }}
              className="text-white/50 hover:text-white text-sm mb-4 inline-flex items-center gap-1"
            >
              ← Back
            </button>
            <h2 className="font-display font-bold text-2xl mb-5">Sign in with email</h2>
            <form onSubmit={handleEmailSubmit} className="flex flex-col gap-4">
              <Input label="Email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              <Input label="Password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              {error && <p className="text-red-400 text-sm">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="mt-1 flex items-center justify-center gap-2 w-full py-3.5 rounded-xl font-semibold text-[15px] bg-brand-green text-[#04240f] hover:brightness-95 active:scale-[0.99] disabled:opacity-50 transition"
              >
                {loading && <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />}
                Sign in
              </button>
              <div className="flex justify-between text-sm text-white/50 pt-1">
                <Link to="/forgot-password" className="hover:text-white">Forgot password?</Link>
                <Link to="/signup" className="text-brand-teal hover:brightness-110">Create account</Link>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
